/* eslint-disable no-labels */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    cloneDeep as _cloneDeep,
    get as _get,
    set as _set,
    has as _has,
    unset as _unset,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
// import FormControlLabel from '@mui/material/FormControlLabel';
// import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckBoxOutlineIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import InfoIcon from '@mui/icons-material/InfoOutlined';

import { NestedDropdown } from 'mui-nested-menu';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import UsersService from '../../../data/users-service';
import RolesService from '../../../data/roles-service';
import {
    MUITree,
} from '../../../data/permissions';
import Auth from '../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    select: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        width: '100%',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 36,
    },
    chip: {
        margin: 2,
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontSize: 16,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255, 255, 255, 0.75)',
        },
    },
    permControl: {
        width: 'calc(100% - ' + theme.getSpacing(3) * 2 + 'px)',
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        marginLeft: theme.spacing(3),
    },
    permTitle: {
        marginLeft: theme.spacing(3),
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    fullWidth: {
        width: '100%',
    },
    treeContainer: {
        width: '100%',
        maxHeight: '420px',
        overflowY: 'scroll',
    },
    treeItem: {
        '& .MuiTreeItem-root': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '& .MuiTreeItem-root:last-child': {
            borderBottom: 'none',
        }
    },
    treeListItem: {
        padding: '2px 0px 2px 5px',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        }
    },
    rolesContainer: {
        width: '100%',
        justifyContent: 'space-between',
        padding: '0 0',
        marginTop: '3px',
        marginBottom: '-5px',
    },
    lightThemeFix: {
        color: '#FFF',
    },
}));

const ROLE_TYPES = ['Read', 'Write', 'Delete', 'Admin', 'Override'];

// Hoisting
const cleanTreeForAppliance = (tree) => {
    const t = { ...tree };

    const cleanTree = (rolesTree, rollingID) => {
        Object.keys(rolesTree).forEach(x => {
            const id = rollingID ? rollingID + '.children.' + x : x;
            if (rolesTree[x].appliance && rolesTree[x].appliance !== process.env.BRICATA_APPLIANCE_TYPE) {
                _unset(t, id);
            } else if (Object.prototype.hasOwnProperty.call(rolesTree[x], 'children')) {
                cleanTree(rolesTree[x].children, id);
            }
        });
    };

    cleanTree(t);
    return t;
};

const CreateRole = () => {
    const theme = useTheme();
    const classes = useStyles();
    const navigate = useNavigate();
    const routeParams = useParams();
    const [isUpdate, setIsUpdate] = useState(false);
    const [role, setRole] = useState({
        rolename: '',
        description: '',
        role_permissions: [],
        role_users: [],
        users: [],
    });
    const [users, setUsers] = useState([]);
    const [roleType, setRoleType] = useState('');
    const [init, setInit] = useState(true);
    const roleForm = useValidate({});
    const [tree, setTree] = useState(_cloneDeep(cleanTreeForAppliance(MUITree)));
    const [expandedList, setExpandedList] = useState([]);
    const [disableUpdateCreate, setDisableUpdateCreate] = useState(false);
    const [assumeRead] = useState(true);
    const usersPerms = Auth.permissions.get('Users');
    const rolesPerms = Auth.permissions.get('Roles');

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        if (routeParams.role) {
            setIsUpdate(true);
            RolesService.getRole(routeParams.role).then((res) => {
                setRole({
                    rolename: res.object.name,
                    description: res.object.description,
                    role_permissions: res.object.role_permissions,
                    role_user: res.object.role_users,
                    users: res.object.role_users,
                });
                if (Object.prototype.hasOwnProperty.call(res.object, 'role_permissions')) {
                    mapToTree(res.object.role_permissions);
                }
            });
        }
        setInit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        UsersService.getUsers().then((users) => {
            setUsers(users.data);
        });
    }, []);

    useEffect(() => {
        let priv = false;
        const flat = flattenTree();

        privCheck:
        for (const k in flat) {
            for (const k2 in flat[k]) {
                if (flat[k][k2] && Auth.permissions.getDescription(k, k2)?.type === 'privileged') {
                    priv = true;
                    break privCheck;
                }
            }
        }

        setRoleType(priv ? 'Privileged' : 'Standard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree]);

    const mapToTree = (perms) => {
        const t = { ...tree };
        const rolesToTree = (rolesTree, rollingID) => {
            Object.keys(rolesTree).forEach(x => {
                const id = rollingID ? rollingID + '.children.' + x : x;
                if (Object.prototype.hasOwnProperty.call(rolesTree[x], 'children')) {
                    rolesToTree(rolesTree[x].children, id);
                } else {
                    const r = rollingID.split('.');
                    try {
                        _set(t, id + '.checked', Boolean(BigInt(perms[x.toLowerCase()]) & BigInt(Auth.permissions._constants[r[r.length - 1]])));
                        handleCheckboxOnClick(null, id, true);
                    } catch (e) {
                        console.log('glm', x, rollingID, id, r);
                        console.log('glm', rolesTree);
                        console.log('glm', Auth.permissions._constants);
                        console.log('glm', 'Error mapping role to tree', e);
                        console.log(r[r.length - 1], 'constant =', Auth.permissions._constants[r[r.length - 1]]);
                    }
                }
            });
        };
        rolesToTree(t);
        setTree(t);
    };

    const handleUpdateRoleData = (e, validID, valid) => {
        roleForm.set({ ...roleForm.get(), [validID]: valid });
        setRole({
            ...role,
            [e.target.getAttribute('dataname')]: e.target.value,
        });
    };

    const handleUpdateRoleUsers = (e) => {
        setRole({
            ...role,
            users: e.target.value,
            role_users: e.target.value,
        });
    };

    const handleRemoveUser = (i) => {
        const r = { ...role };
        r.users.splice(i, 1);
        r.role_users = r.users;
        setRole(r);
    };

    const flattenTree = () => {
        const flat = {};

        // Flatten the tree
        const nidhogg = (t, n) => {
            if (Object.prototype.hasOwnProperty.call(t, 'children')) {
                flat[n] = {};
                Object.keys(t.children).forEach(x => {
                    if (!Object.prototype.hasOwnProperty.call(t.children[x], 'children')) {
                        flat[n] = {
                            ...flat[n],
                            [x]: t.children[x].checked ? t.children[x].checked : false,
                        };
                    } else {
                        nidhogg(t.children[x], x);
                    }
                });
            }
        };

        // Kick off the recursion
        Object.keys(tree).forEach(v => {
            nidhogg(tree[v], v);
        });

        // Prune for sanity
        Object.keys(flat).forEach(v => {
            if (Object.keys(flat[v]).length === 0) delete flat[v];
        });

        return flat;
    };

    const handleCreateRole = () => {
        setDisableUpdateCreate(true);
        toast.info((isUpdate ? 'Updating' : 'Creating') + ' role...');

        const flat = flattenTree();

        // Calculate
        const sum = Object.keys(flat).reduce((a, c) => {
            for (const x in flat[c]) {
                if (flat[c][x]) {
                    a[x] = a[x] | BigInt(Auth.permissions._constants[c]);
                }
            }
            return a;
        }, {
            Read: BigInt(0),
            Write: BigInt(0),
            Delete: BigInt(0),
            Admin: BigInt(0),
            Override: BigInt(0),
        });

        const rolePermissions = Object.keys(sum).reduce((a, c) => { a[c] = sum[c].toString(); return a }, {});

        if (isUpdate) {
            RolesService.updateRole(routeParams.role, { ...role, permissions: rolePermissions }).then(() => {
                navigate('/system/users_roles', { state: { tab: 1 } });
                toast.success('Role updated successfully');
            }, err => {
                if (err && Object.prototype.hasOwnProperty.call(err, 'Message') && err.Message.includes('duplicate')) {
                    toast.error('Role name is already taken...');
                } else if (typeof err?.message === 'string' || typeof err?.Message === 'string') {
                    toast.error(err.message || err.Message);
                } else if (typeof err === 'string') {
                    toast.error(err);
                } else {
                    toast.error('Cannot update role');
                }
            }).then(() => {
                setDisableUpdateCreate(false);
            });
        } else {
            RolesService.createRole({ ...role, role_permissions: rolePermissions }).then(() => {
                navigate('/system/users_roles', { state: { tab: 1 } });
                toast.success('Role created successfully');
            }, err => {
                if (err.status === 403) {
                    toast.error('Insufficient permissions to create this role');
                } else if (err.status === 409) {
                    toast.error('Role name is already taken...');
                } else if (typeof err?.message === 'string' || typeof err?.Message === 'string') {
                    toast.error(err.message || err.Message);
                } else if (typeof err === 'string') {
                    toast.error(err);
                } else {
                    toast.error('Cannot create role');
                }
            }).then(() => {
                setDisableUpdateCreate(false);
            });
        }
    };

    const handleCancel = () => {
        navigate('/system/users_roles', { state: { tab: 1 } });
    };

    const handleExpandListToggle = (e, item) => {
        const l = [...expandedList];
        if (l.includes(item)) {
            const i = l.indexOf(item);
            l.splice(i, 1);
        } else {
            l.push(item);
        }
        setExpandedList(l);
    };

    const handleCollapseAll = () => {
        setExpandedList([]);
    };

    const handleExpandAll = () => {
        const a = [];

        const weHaveToGoDeeper = (rolesTree, rollingID) => {
            Object.keys(rolesTree).forEach(x => {
                const id = rollingID ? rollingID + '_' + x : x;
                if (Object.prototype.hasOwnProperty.call(rolesTree[x], 'children')) {
                    a.push(id);
                    weHaveToGoDeeper(rolesTree[x].children, id);
                } else {
                    a.push(x);
                }
            });
        };
        weHaveToGoDeeper(tree);
        setExpandedList(a);
    };

    const handleEnableDisableAll = (type, action) => {
        const t = { ...tree };

        const enableAllOfType = (rolesTree, rollingID) => {
            Object.keys(rolesTree).forEach(x => {
                const id = rollingID ? rollingID + '.children.' + x : x;
                if (Object.prototype.hasOwnProperty.call(rolesTree[x], 'children')) {
                    enableAllOfType(rolesTree[x].children, id);
                } else {
                    if (x === type || type === 'All') {
                        if (type !== 'Read' && action) {
                            const idSplit = id.split('.');
                            idSplit.splice(-1);
                            idSplit.push('Read');
                            const idJoin = idSplit.join('.');
                            if (_has(t, idJoin)) {
                                _set(t, idJoin + '.checked', true);
                            }
                        }
                        _set(t, id + '.checked', action);
                    }
                    handleCheckboxOnClick(null, id, true);
                }
            });
        };

        enableAllOfType(t);
        setTree(t);
    };

    const handleCheckboxOnClick = (e, id, dashCheckOnly) => {
        if (e) {
            e.stopPropagation();
        }

        id = id.replaceAll('_', '.children.');
        const t = { ...tree };

        // Top down
        const dashCheckDown = (parentChecked, nID) => {
            Object.keys(_get(t, nID + '.children', {})).forEach((x) => {
                _set(t, nID + '.children.' + x + '.checked', parentChecked);
                if (parentChecked) _set(t, nID + '.children.' + x + '.dashed', false);
                dashCheckDown(parentChecked, nID + '.children.' + x);
            });
        };

        // Bottom up
        const dashCheckUp = (nID) => {
            let checkCount = 0;
            let childCount = 0;
            let dashCount = 0;
            let dashed = false;
            if (Object.prototype.hasOwnProperty.call(_get(t, nID, {}), 'children')) {
                Object.keys(_get(t, nID + '.children', {})).forEach((x) => {
                    let checked;
                    ++childCount;
                    const tn = _get(t, nID + '.children.' + x);
                    if (tn.checked) ++checkCount;
                    if (tn.dashed) ++dashCount;
                    if (dashCount) {
                        dashed = true;
                        checked = false;
                    } else if (!checkCount) {
                        dashed = false;
                        checked = false;
                    } else if (checkCount !== childCount) {
                        dashed = true;
                        checked = false;
                    } else if (checkCount === childCount) {
                        dashed = false;
                        checked = true;
                    }
                    _set(t, nID + '.dashed', dashed);
                    if (checked !== undefined) _set(t, nID + '.checked', checked);
                    const n = nID.split('.');
                    dashCheckUp(n.splice(0, n.length - 2).join('.'));
                });
            } else if (nID) {
                const n = nID.split('.');
                dashCheckUp(n.splice(0, n.length - 2).join('.'));
            }
        };

        if (!dashCheckOnly) {
            const checkState = !_get(t, id + '.checked', false);
            if (assumeRead) {
                const idSplit = id.split('.');
                const roleTypeCheck = idSplit.splice(-1)[0];
                const readCheck = idSplit.join('.') + '.Read';
                const writeCheck = idSplit.join('.') + '.Write';
                const deleteCheck = idSplit.join('.') + '.Delete';
                const adminCheck = idSplit.join('.') + '.Admin';
                const overrideCheck = idSplit.join('.') + '.Override';
                if (ROLE_TYPES.includes(roleTypeCheck)) {
                    if (_has(t, readCheck)) {
                        if (roleTypeCheck !== 'Read' && checkState) {
                            _set(t, readCheck + '.checked', checkState);
                        } else if (roleTypeCheck !== 'Read' && !_get(t, readCheck + '.checked', false)) {
                            if (checkState) _set(t, readCheck + '.checked', true);
                        } else if (roleTypeCheck === 'Read' && !checkState) {
                            if (_has(t, writeCheck)) _set(t, writeCheck + '.checked', false);
                            if (_has(t, deleteCheck)) _set(t, deleteCheck + '.checked', false);
                            if (_has(t, adminCheck)) _set(t, adminCheck + '.checked', false);
                            if (_has(t, overrideCheck)) _set(t, overrideCheck + '.checked', false);
                        }
                    }
                }
            }
            _set(t, id + '.checked', checkState);
            dashCheckDown(checkState, id);
        }
        dashCheckUp(id);
        setTree(t);
    };

    const createRolesTree = useCallback((rolesTree, rollingID) => {
        const elements = [];

        const buildRoleTree = (v, i) => {
            const id = rollingID ? rollingID + '_' + v : v;
            let desc = null;
            if (ROLE_TYPES.includes(v)) {
                const i = rollingID.split('_');
                desc = Auth.permissions.getDescription(i[i.length - 1], v);
            }

            const a = MC2CHECK ? 'mc2' : 'cmc';
            const isOurs = desc => !desc?.appliance || (desc?.appliance === a);

            const label = (desc && isOurs(desc)) || isOurs(rolesTree[v])
                ? (<div className={classes.rolesContainer}>
                    <Box display="flex" width={'100%'} justifyContent={'space-between'}>
                        <Typography variant={desc ? 'body2' : 'body1'}>
                            {rolesTree[v].title === 'Sensor' ? 'OpenText™ NDR' : (desc?.description || rolesTree[v].title)}
                        </Typography>
                        <Box onClick={e => { handleCheckboxOnClick(e, id) }} style={{ margin: desc ? '5px 0 -5px' : '0' }}>
                            <Box color={
                                rolesTree[v].dashed ? theme.palette.secondary.main
                                    : rolesTree[v].checked ? theme.palette.primary.main
                                        : theme.palette.primary.text
                            }>
                                {
                                    rolesTree[v].dashed ? <IndeterminateCheckBoxIcon />
                                        : rolesTree[v].checked ? <CheckBoxIcon />
                                            : <CheckBoxOutlineIcon />
                                }
                            </Box>
                        </Box>
                    </Box>
                    {desc && <Box mt={'-12px'} pl={'10px'}>
                        <Typography variant="caption">{rolesTree[v].title} - {desc?.type}</Typography>
                    </Box>}
                </div>) : null;

            label && elements.push(
                Object.prototype.hasOwnProperty.call(rolesTree[v], 'children') ? (
                    <TreeItem key={id} itemId={id} label={label} className={classes.treeListItem}>
                        {createRolesTree(rolesTree[v].children, id)}
                    </TreeItem>
                ) : (
                    <TreeItem key={id} itemId={id} label={label} className={classes.treeListItem} />
                ),
            );
        };

        if (Object.prototype.hasOwnProperty.call(rolesTree, 'Read')) {
            Object.keys(rolesTree).forEach(buildRoleTree);
        } else {
            Object.keys(rolesTree).sort().forEach(buildRoleTree);
        }

        return elements;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleCheckboxOnClick]);

    // const handleAssumeRead = (e) => {
    //     setAssumeRead(e.currentTarget.checked);

    //     if (e.currentTarget.checked) {
    //         const t = { ...tree };
    //         const checkReadNecessary = (rolesTree, rollingID) => {
    //             Object.keys(rolesTree).forEach(x => {
    //                 const id = rollingID ? rollingID + '.children.' + x : x;
    //                 if (Object.prototype.hasOwnProperty.call(rolesTree[x], 'children')) {
    //                     checkReadNecessary(rolesTree[x].children, id);
    //                 } else {
    //                     if (ROLE_TYPES.includes(x) && x !== 'Read') {
    //                         if (_get(t, id + '.checked', false)) {
    //                             const idSplit = id.split('.');
    //                             idSplit.splice(-1);
    //                             idSplit.push('Read');
    //                             _set(t, idSplit.join('.') + '.checked', true);
    //                         }
    //                     }
    //                     handleCheckboxOnClick(null, id, true);
    //                 }
    //             });
    //         };

    //         checkReadNecessary(t);
    //     }
    // };

    const enableDisableMenus = {
        label: 'Toggle',
        items: [
            {
                label: 'All',
                items: [
                    { label: 'Enable All', callback: (e) => { handleEnableDisableAll('All', true) } },
                    { label: 'Disable All', callback: (e) => { handleEnableDisableAll('All', false) } },
                ],
            },
            {
                label: 'Read',
                items: [
                    { label: 'Enable All Read', callback: (e) => { handleEnableDisableAll('Read', true) } },
                    { label: 'Disable All Read', callback: (e) => { handleEnableDisableAll('Read', false) } },
                ],
            },
            {
                label: 'Write',
                items: [
                    { label: 'Enable All Write', callback: (e) => { handleEnableDisableAll('Write', true) } },
                    { label: 'Disable All Write', callback: (e) => { handleEnableDisableAll('Write', false) } },
                ],
            },
            {
                label: 'Delete',
                items: [
                    { label: 'Enable All Delete', callback: (e) => { handleEnableDisableAll('Delete', true) } },
                    { label: 'Disable All Delete', callback: (e) => { handleEnableDisableAll('Delete', false) } },
                ],
            },
            {
                label: 'Admin',
                items: [
                    { label: 'Enable All Admin', callback: (e) => { handleEnableDisableAll('Admin', true) } },
                    { label: 'Disable All Admin', callback: (e) => { handleEnableDisableAll('Admin', false) } },
                ],
            },
            {
                label: 'Override',
                items: [
                    { label: 'Enable All Override', callback: (e) => { handleEnableDisableAll('Override', true) } },
                    { label: 'Disable All Override', callback: (e) => { handleEnableDisableAll('Override', false) } },
                ],
            },
        ],
    };

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    return (
        <Grid container spacing={2} direction="row" justifyContent="center" alignItems="center">
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={5}>
                            <Grid item xs={6}>
                                <Validator
                                    disabled={!rolesPerms.write}
                                    errorText="Role name cannot be empty"
                                    label="Name"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    kick={init}
                                    value={role.rolename}
                                    inputProps={{ dataname: 'rolename' }}
                                    onChange={handleUpdateRoleData}
                                />
                                <Validator
                                    disabled={!rolesPerms.write}
                                    errorText="Role description cannot be empty"
                                    label="Description"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    kick={init}
                                    value={role.description}
                                    inputProps={{ dataname: 'description' }}
                                    onChange={handleUpdateRoleData}
                                />
                                <TextField
                                    disabled
                                    label="Role Type"
                                    value={roleType}
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                />
                                {usersPerms.write && <FormControl className={classes.select}>
                                    <InputLabel id="role-select-label">Users</InputLabel>
                                    <Select
                                        disabled={!rolesPerms.write}
                                        labelId="role-select-label"
                                        id="role-select"
                                        value={role.users}
                                        multiple
                                        onChange={handleUpdateRoleUsers}
                                        input={<Input id="role-select-input" />}
                                        renderValue={(rolesSelected) => (
                                            <div className={classes.chips}>
                                                {rolesSelected.map((v, i) => {
                                                    return <Chip
                                                        key={'user-chip-' + i}
                                                        label={v}
                                                        className={classes.chip}
                                                        onMouseDown={e => { e.stopPropagation() }}
                                                        onDelete={() => { handleRemoveUser(i) }} />;
                                                })}
                                            </div>
                                        )}
                                        MenuProps={MenuProps}
                                    >
                                        {users.map((v, i) => (<MenuItem key={'user-select-' + i} value={v.username}>{v.username} - {v.name}</MenuItem>))}
                                    </Select>
                                </FormControl>}
                            </Grid>
                            <Grid item xs={6}>
                                <Box py={2} pr={2}>
                                    <Paper elevation={3}>
                                        <Box p={2}>
                                            <Box mb={1} display="flex" justifyContent="space-between" width={'100%'}>
                                                <Typography variant="h5">Permissions</Typography>
                                                <Box display="flex">
                                                    <Box mt={-1}>
                                                        <Tooltip
                                                            classes={{ tooltip: classes.lightThemeFix }}
                                                            title="If read is not enabled and another permission is enabled, read will be enabled automatically. If read is disabled all permissions for the category will be disabled.">
                                                            <IconButton><InfoIcon /></IconButton>
                                                            {/* <FormControlLabel
                                                                label={'Assume Read'}
                                                                control={<Checkbox checked={assumeRead} onChange={handleAssumeRead} />}
                                                            /> */}
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box mb={1} display="flex" justifyContent="flex-end" width={'100%'}>
                                                <Box display="flex">
                                                    <Box ml={2}>
                                                        <NestedDropdown
                                                            menuItemsData={enableDisableMenus}
                                                            ButtonProps={{ variant: 'contained' }}
                                                        />
                                                    </Box>
                                                    <Box ml={2}><Button color='secondary' variant='contained' aria-label='collapse all' onClick={handleCollapseAll}>Collapse All</Button></Box>
                                                    <Box ml={2}><Button color='secondary' variant='contained' aria-label='expand all' onClick={handleExpandAll}>Expand All</Button></Box>
                                                </Box>
                                            </Box>
                                            <FormControl className={classes.fullWidth}>
                                                <Grid container>
                                                    <Box className={classes.treeContainer}>
                                                        <Paper elevation={3}>
                                                            <SimpleTreeView
                                                                expandedItems={expandedList}
                                                                onItemExpansionToggle={handleExpandListToggle}
                                                                sx={{
                                                                    width: '100%',
                                                                }}
                                                                slots={{
                                                                    collapseIcon: ExpandMoreIcon,
                                                                    expandIcon: ChevronRightIcon,
                                                                }}
                                                            >
                                                                {createRolesTree(tree)}
                                                            </SimpleTreeView>
                                                        </Paper>
                                                    </Box>
                                                </Grid>
                                            </FormControl>
                                        </Box>
                                    </Paper>
                                </Box>
                            </Grid>
                            {rolesPerms.write && <Grid item container xs={12} justifyContent="flex-end">
                                <Box><Button variant="contained" color="secondary" aria-label="Cancel" onClick={handleCancel}>Cancel</Button></Box>
                                <Box ml={2}><Button
                                    variant="contained"
                                    color="primary"
                                    aria-label={isUpdate ? 'Update' : 'Create'}
                                    onClick={handleCreateRole}
                                    disabled={!checkValidity(roleForm.get()) || disableUpdateCreate}
                                >
                                    {isUpdate ? 'Update' : 'Create'}
                                </Button></Box>
                            </Grid>}
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default CreateRole;
